import { useDispatch } from 'react-redux';

import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { Dispatch } from '@swf/core/store';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFCitizenship from '../components/RMFCitizenship/RMFCitizenship';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFLossLimit from '../components/RMFLossLimit/RMFLossLimit';
import RMFOccupation from '../components/RMFOccupation/RMFOccupation';
import RMFOccupationPEP from '../components/RMFOccupationPEP/RMFOccupationPEP';
import RMFPendingVerification from '../components/RMFPendingVerification/RMFPendingVerification';
import RMFPhone from '../components/RMFPhone/RMFPhone';
import RMFPhoneVerification from '../components/RMFPhoneVerification/RMFPhoneVerification';
import RMFSummaryScreen from '../components/RMFSummaryScreen/RMFSummaryScreen';
import RMFTermsOfService from '../components/RMFTermsOfService/RMFTermsOfService';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfCAONFlow = (): FlowConfig => {
  const { modal } = useDispatch<Dispatch>();
  const exitWizard = useWizardExit();

  const { invokeSignupDob, forkDobCheck, forkRmfExit } = useRMFBaseSteps();

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    RMFLossLimit,
    RMFDepositLimit
  );
  const forkPhoneVerificationCheck = new Fork(
    Guard.PhoneVerificationCheck,
    [RMFPhoneVerification, RMFAddress],
    [RMFAddress]
  );
  const invokeMobilePhoneSave = new Invoke(
    Service.RmfMobilePhoneSave,
    forkPhoneVerificationCheck,
    RMFPhone
  );
  const invokeAliasSave = new Invoke(Service.RmfAliasSave, RMFPhone, RMFLegalName);
  const invokeMiddleNameSave = new Invoke(Service.RmfMiddleNameSave, invokeAliasSave, RMFLegalName);
  const invokeLegalNameSave = new Invoke(
    Service.RmfLegalNameSave,
    invokeMiddleNameSave,
    RMFLegalName
  );
  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFOccupationPEP, RMFAddress);
  const invokeOccupationPEPSave = new Invoke(
    Service.RmfOccupationPEPSave,
    RMFCitizenship,
    RMFOccupationPEP
  );
  const invokeOccupationSave = new Invoke(
    Service.RmfOccupationSave,
    RMFTermsOfService,
    RMFOccupation
  );

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const invokeSetJumpToDeposit = new Invoke(Service.SetJumpCreator, jumpToFeatureStep);

  const forkPendingVerification = new Fork(
    Guard.ShowPendingVerification,
    [RMFPendingVerification],
    [RMFVerification, forkRmfExit]
  );
  const forkCheckRMSuspended = new Fork(
    Guard.CheckRMSuspended,
    [
      {
        name: 'rmfExit',
        entry: () => {
          exitWizard();
          modal.enqueue({ path: '/security-investigation' });
        },
        type: 'final'
      }
    ],
    [{ name: 'rmfExitDeposit', entry: Action.GoNext }]
  );
  const forkCheckUserFlags = new Fork(
    Guard.CheckUserFlags,
    [forkPendingVerification],
    [forkCheckRMSuspended]
  );

  const invokeAccountFetch = new Invoke(
    Service.RmfAccountFetch,
    forkCheckUserFlags,
    invokeSetJumpToDeposit
  );

  const invokeRetryEnableRealMoneyFlag = new Invoke(
    Service.RmfEnableRealMoneyFlag,
    invokeAccountFetch,
    forkPendingVerification
  );

  const invokeRetryCAONDataSave = new Invoke(
    Service.RmfRetryCAONUserDataSave,
    invokeRetryEnableRealMoneyFlag,
    RMFSummaryScreen
  );

  const forkEKYCElectronicCheckFailed = new Fork(
    Guard.CheckEKYCRetry,
    [RMFSummaryScreen, invokeRetryCAONDataSave],
    forkPendingVerification
  );

  const forkEKYCFailCheckLimitsSkipped = new Fork(
    Guard.CheckEKYCFailedRetry,
    forkEKYCElectronicCheckFailed,
    RMFDepositLimit
  );

  const forkEKYCFailCheckLimitSelected = new Fork(
    Guard.CheckEKYCFailedRetry,
    forkEKYCElectronicCheckFailed,
    RMFLossLimit
  );

  const invokeEnableRealMoneyFlagWithLimitsSkipped = new Invoke(
    Service.RmfEnableRealMoneyFlag,
    invokeAccountFetch,
    forkEKYCFailCheckLimitsSkipped
  );

  const invokeEnableRealMoneyFlagWithLimitSelected = new Invoke(
    Service.RmfEnableRealMoneyFlag,
    invokeAccountFetch,
    forkEKYCFailCheckLimitSelected
  );

  const invokeLegalBoxesSave = new Invoke(
    Service.RmfTermsOfServiceSave,
    invokeEnableRealMoneyFlagWithLimitSelected,
    RMFLossLimit
  );

  const invokeLossLimitSave = new Invoke(Service.RmfLossLimitSave, invokeLegalBoxesSave);

  const invokeRmfTermsOfServiceWithoutLimitSave = new Invoke(
    Service.RmfTermsOfServiceWithoutLimitSave,
    invokeEnableRealMoneyFlagWithLimitsSkipped,
    RMFDepositLimit
  );

  const forkDepositLimits = new Fork(
    Guard.LimitsSelected,
    [invokeRmfTermsOfServiceWithoutLimitSave, invokeEnableRealMoneyFlagWithLimitsSkipped],
    [
      invokeDepositLimitSave,
      RMFLossLimit,
      invokeLossLimitSave,
      invokeLegalBoxesSave,
      invokeEnableRealMoneyFlagWithLimitSelected
    ]
  );

  const invokeCitizenshipSave = new Invoke(
    Service.RmfCitizenshipSave,
    RMFOccupation,
    RMFCitizenship
  );

  return [
    invokeSignupDob,
    forkDobCheck,
    RMFLegalName,
    invokeLegalNameSave,
    invokeMiddleNameSave,
    invokeAliasSave,
    RMFPhone,
    invokeMobilePhoneSave,
    forkPhoneVerificationCheck,
    invokeAddressSave,
    RMFOccupationPEP,
    invokeOccupationPEPSave,
    RMFCitizenship,
    invokeCitizenshipSave,
    RMFOccupation,
    invokeOccupationSave,
    RMFTermsOfService,
    RMFDepositLimit,
    forkDepositLimits,
    forkEKYCFailCheckLimitsSkipped,
    forkEKYCFailCheckLimitSelected,
    forkEKYCElectronicCheckFailed,
    invokeAccountFetch,
    invokeSetJumpToDeposit,
    jumpToFeatureStep,
    forkCheckUserFlags
  ];
};

export default useRmfCAONFlow;
